import { useState, useMemo } from 'react';
import {
  Stack,
  Paper,
  Table,
  Text,
  Group,
  Badge,
  TextInput,
  Skeleton,
} from '@mantine/core';
import { IconSearch, IconTruckDelivery, IconCheck, IconX } from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import { useDeliveryHistory as useDeliveryHistoryApi } from '@/hooks/usePortalApi';
import dayjs from 'dayjs';

interface DeliveryHistoryItem {
  id: number;
  delivery_date: string;
  route_code: string;
  items_delivered: number;
  total_amount: string;
  signature_captured: boolean;
}

type SortField =
  | 'delivery_date'
  | 'route_code'
  | 'items_delivered'
  | 'total_amount'
  | 'signature_captured';

export default function DeliveryHistory() {
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('delivery_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const { data, isLoading } = useDeliveryHistoryApi();
  const items: DeliveryHistoryItem[] = (data?.items ?? []) as DeliveryHistoryItem[];

  const filtered = useMemo(() => {
    let result = [...items];
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(
        (r) =>
          r.route_code.toLowerCase().includes(s) ||
          r.delivery_date.toLowerCase().includes(s) ||
          String(r.items_delivered).includes(s) ||
          r.total_amount.includes(s),
      );
    }
    result.sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[sortField];
      const bv = (b as unknown as Record<string, unknown>)[sortField];
      const cmp = (av as string) < (bv as string) ? -1 : (av as string) > (bv as string) ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [items, search, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIndicator = (field: SortField) =>
    sortField === field ? (sortDir === 'asc' ? ' \u2191' : ' \u2193') : '';

  return (
    <Stack gap="lg">
      <PageHeader
        title="Delivery History"
        description="Past deliveries and proof of delivery records"
      />

      <TextInput
        placeholder="Search by route code or date..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />

      {isLoading ? (
        <Stack gap="xs">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} height={50} radius="md" />
          ))}
        </Stack>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<IconTruckDelivery size={30} />}
          title="No delivery history"
          description={
            search
              ? 'No deliveries match your search criteria'
              : 'No past deliveries have been recorded yet'
          }
        />
      ) : (
        <Paper>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('delivery_date')}
                >
                  Delivery Date{sortIndicator('delivery_date')}
                </Table.Th>
                <Table.Th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('route_code')}
                >
                  Route Code{sortIndicator('route_code')}
                </Table.Th>
                <Table.Th
                  ta="right"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('items_delivered')}
                >
                  Items Delivered{sortIndicator('items_delivered')}
                </Table.Th>
                <Table.Th
                  ta="right"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('total_amount')}
                >
                  Total Amount{sortIndicator('total_amount')}
                </Table.Th>
                <Table.Th
                  ta="center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('signature_captured')}
                >
                  Signature{sortIndicator('signature_captured')}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {dayjs(row.delivery_date).format('MMM D, YYYY')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="outline" size="sm">
                      {row.route_code}
                    </Badge>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm">{row.items_delivered}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={500}>
                      ${parseFloat(row.total_amount).toFixed(2)}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="center">
                    {row.signature_captured ? (
                      <Group justify="center">
                        <Badge
                          size="sm"
                          variant="light"
                          color="green"
                          leftSection={<IconCheck size={12} />}
                        >
                          captured
                        </Badge>
                      </Group>
                    ) : (
                      <Group justify="center">
                        <Badge
                          size="sm"
                          variant="light"
                          color="gray"
                          leftSection={<IconX size={12} />}
                        >
                          missing
                        </Badge>
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
